import React, { useEffect, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCTextArea, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import CheckPermission from "src/components/CheckPermission";
import Photo from "src/components/Photo";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import formatDate from "src/utils/formatDate";
import { v4 } from "uuid";
import { IComment, IComponentComments, IUsersComment } from "./interface";
import { AnswerDiv, ColumnFlex, CommentsDiv, ContainerUser, PaddingDiv, SpanComment, TextAreaAnswering, UlUsers } from "./styles";

const Comment: React.FC<IComponentComments> = ({
	defaultValue,
	names: {
		id,
		commentary,
		userId,
		userImage,
		userName
	},
	onAddComment,
	onDeleteComment,
	disabled
}) => {
	const {addToast} = RCCHookToast();
	const { REACT_APP_S3_ADDRESS, REACT_APP_S3_DIRECTORY } = process.env;
	const { user, administrator, superUser } = useAuth();
	const {confirm} = useConfirmModal();
	const {translate, currentLanguage} = useTranslation();	
	const { watch, register, setValue } = useForm();

	const [users, setUsers] = useState<IUsersComment[]>([]);
	const [filteredUsers, setFilteredUsers] = useState<IUsersComment[]>([]);
	const [showUserSelection, setShowUserSelection] = useState(false);
	const [lastCharInserted, setLastCharInserted] = useState("");
	const [positionLastCharInserted, setPositionLastCharInserted] = useState(0);
	const [filterUsers, setFilterUsers] = useState(""); 
	const [filterUsersCharNumber, setFilterUsersCharNumber] = useState(0);
	const ulUsersRef = useRef<HTMLDivElement>(null);
	const [classHovered, setClassHovered] = useState<number>(0);

	const convertDefaultDataToCommentState = (defaultVal: Array<any>): IComment[] => defaultVal.map((dfvalue) => ({
		id: dfvalue[id],
		comment: dfvalue[commentary],
		efa005Id: dfvalue[userId],
		efa005Nome: dfvalue[userName],
		efa005Imagem: dfvalue[userImage],
		children: convertDefaultDataToCommentState(dfvalue.children),
		dateCreated: dfvalue.dateCreated
	}));

	const answerDivRef = useRef<HTMLDivElement>(null);
	const lastItemSpanRef = useRef<HTMLDivElement>(null);
	const [comments, setComments] = useState<IComment[]>(defaultValue ? convertDefaultDataToCommentState(defaultValue) : []);
	const [commentAnswerId, setCommentAnswerId] = useState(0);
	const [loadingButton, setLoadingButton] = useState(false);

	useEffect(() => {
		lastItemSpanRef.current?.scrollIntoView();
	},[comments]);

	useEffect(() => {
		setComments(defaultValue ? convertDefaultDataToCommentState(defaultValue) : []);
	}, [defaultValue]);

	const answerComment = (idx: number) => {
		setCommentAnswerId((prevState) => prevState === idx ? 0 : idx);
	};

	const submitDelete = (idx: number) => {	
		confirm(
			translate("Exclusão de comentário"), 
			`${translate("Deseja realmente excluir o comentário")}?`,
			async () => {
				await onDeleteComment(idx);
				addToast({
					title: translate("Sucesso"),
					description: translate("Comentário excluído com sucesso"),
					color: "green"
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir comentário"), addToast);
			},
			true
		);
	};

	const recursivelyRenderComments = (comment: IComment) => (
		<RCCGrid
			key={`${comment.id}-comment`}
			container
			item
			direction="column"
		>
			<div style={{display: "flex", alignItems: "center", position: "relative"}}>
				<Photo 
					hover={false}
					name={comment.efa005Nome}
					link={comment.efa005Imagem && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${comment.efa005Imagem}`}
					width={40}
					height={40}
				/>
				<ColumnFlex>
					<ContainerUser>
						<Title color="purple" size="md" styleInline="margin: 2px 5px 2px 5px;">
							{comment.efa005Nome}
						</Title>
						<Title color="grey" size="xs" styleInline="margin: 2px 5px 2px 5px;">
							{translate("em")} {formatDate(comment.dateCreated, currentLanguage, true)}
						</Title>
					</ContainerUser>
				</ColumnFlex>
				<div style={{ display: "flex"}}>
					<CheckPermission minimumPermissionRequired={PermissionEnum.VIEW}>
						<RCCTooltip
							color={commentAnswerId === comment.id ? "blue" : "purple"}
							text={translate("Responder comentário")}
							position="leftEnd"
						>
							<RCCButton
								color={commentAnswerId === comment.id ? "blue" : "purple"}
								icon={{
									name: "reply"
								}}
								layout="isRounded"
								size="md"
								onClick={() => answerComment(comment.id)}
								disabled={disabled}
							/>
						</RCCTooltip>
					</CheckPermission>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
						<RCCTooltip
							color="red"
							text={translate("Excluir comentário")}
							position="leftEnd"
						>
							<RCCButton
								color="red"
								icon={{
									name: "trash"
								}}
								layout="isRounded"
								size="md"
								onClick={() => submitDelete(comment.id)}
								disabled={(!administrator && !superUser && user.efa005Id !== comment.efa005Id) || disabled}
							/>
						</RCCTooltip>
					</CheckPermission>
				</div>
			</div>

			<SpanComment isBlue={commentAnswerId === comment.id} dangerouslySetInnerHTML={{__html: comment.comment.replace(/@([^ ]+)/g, "<span class=\"highlighted\">@$1</span>")}}></SpanComment>
			{comment.children.length > 0 && (
				<PaddingDiv>
					{comment.children.map((child) => recursivelyRenderComments(child))}
				</PaddingDiv>
			)}
		</RCCGrid>
	);

	const onSubmit = async () => {
		const comment = watch("answerComment");
		if(!comment || comment === ""){
			addToast({
				title: translate("Erro"),
				description: translate("É necessário inserir um comentário"),
				color: "yellow"
			});
			return;
		}
		setLoadingButton(true);
		await onAddComment(comment, commentAnswerId !== 0 ? commentAnswerId : undefined);
		setValue("answerComment", "");
		answerDivRef.current?.querySelectorAll("textarea")[0].focus();
		setCommentAnswerId(0);
		setLoadingButton(false);
	};

	const loadUsers = async () => {
		setFilterUsers("");
		if(users.length > 0)
			return;

		const {data} = await RCCServicesApi.getRequest({
			method: "User/CommentUsers"
		});
		setUsers(data as IUsersComment[]);
	};

	const onChangeComment = async (e: any) => {
		setPositionLastCharInserted(e.target.selectionStart);
		setLastCharInserted(e.key);
		if(showUserSelection && e.key === " "){
			setShowUserSelection(false);
			return;
		}
		
		if(e.key === "@"){
			await loadUsers();
			setShowUserSelection(true);
			return;
		}

		if(showUserSelection && e.key !== " "){
			setFilterUsersCharNumber((prevState) => prevState + 1);
			setFilterUsers((prevState) => prevState + e.key);
		}
	};

	const onChangeCommentSpecialChars = async (e: any) => {
		if(e.key === "Backspace"){
			setPositionLastCharInserted((prevState) => prevState - 1);
			setFilterUsers((prevState) => prevState.slice(0, -1));
			if(lastCharInserted === "@")
				setShowUserSelection(false);
			return;
		}
		if(e.key === "ArrowDown"){
			e.preventDefault();
			setClassHovered((prevState) => prevState + 1);
			return;
		}
		if(e.key === "ArrowUp"){
			e.preventDefault();
			if(classHovered !== -1)
				setClassHovered((prevState) => prevState - 1);
			return;
		}
		if(e.key === "Enter" && showUserSelection){
			e.preventDefault();

			if(classHovered <= 0)
				return;

			const buttons = ulUsersRef.current?.querySelectorAll("button");

			if(!buttons)
				return;

			const button = buttons[classHovered];

			if(!button)
				return;

			const textButton = button.textContent;

			if(textButton === null)
				return;
				
			addUserToComment({
				efa005Email: `[${textButton.substring(textButton.lastIndexOf("[") + 1, textButton.lastIndexOf("]"))}] `,
				efa005Id: 0,
				efa005Imagem: "",
				efa005Nome: ""
			});
		}
	};

	const addUserToComment = (userComment: IUsersComment) => {
		setShowUserSelection(false);
		const response = String(watch("answerComment"));
		const newResponse = response.slice(0, positionLastCharInserted + 1 - filterUsersCharNumber) + userComment.efa005Email + response.slice(positionLastCharInserted + 1, response.length);
		setValue("answerComment", newResponse);
		setFilterUsersCharNumber(0);
		// const textareaHTML = `${response.slice(0, positionLastCharInserted + 1 - filterUsersCharNumber)} <span className="highlight">${userComment.efa005Email}</span> ${response.slice(positionLastCharInserted + 1, response.length)}`;
		// if(answerDivRef.current)
		// 	answerDivRef.current.querySelectorAll("textarea")[0].innerHTML = textareaHTML;
	};

	useEffect(() => {
		if(filterUsers.trim() === ""){
			setFilteredUsers(users);
			return;
		}
		setFilteredUsers(users.filter((userComment) => userComment.efa005Nome.includes(filterUsers) || userComment.efa005Email.includes(filterUsers)));
	}, [filterUsers, users]);

	useEffect(() => {
		if(ulUsersRef.current && classHovered > -1){
			ulUsersRef.current.scrollTop = 22 * classHovered;
		}
	}, [classHovered]);

	return(
		<RCCGrid style={{height: "93%"}}>
			<CommentsDiv>
				{comments.length === 0 ? (
					<RCCAlert 
						title={translate("Atenção")}
						description={translate("Não foram encontrados comentários")}
					/>
				): comments.map((comment: IComment) => recursivelyRenderComments(comment))}	
				<div ref={lastItemSpanRef}></div>				
			</CommentsDiv>
			<AnswerDiv 
				ref={answerDivRef}
			>
				<RCCTextArea 
					name="answerComment"
					label={commentAnswerId ? translate("Responder comentário") : translate("Adicionar comentário")}
					maxLength={4000}
					register={register}
					styleInline={commentAnswerId ? TextAreaAnswering : ">textarea{ max-height: 64px !important; } margin-top: -2px;"}
					onKeyPress={onChangeComment}
					onKeyDown={onChangeCommentSpecialChars}
					disabled={disabled}
				/>
				<UlUsers 
					ref={ulUsersRef}
					style={showUserSelection ? {} : {display: "none"}}
					onMouseEnter={() => setClassHovered(-1)}
				>
					{filteredUsers.map((userComment, index) => <button className={classHovered === index ? "hovered" : ""} type="button" key={v4()} 
						onClick={() => addUserToComment(userComment)}
					>
						<Photo 
							name={userComment.efa005Nome}
							link={userComment.efa005Imagem && `${REACT_APP_S3_ADDRESS}/${REACT_APP_S3_DIRECTORY}/imagens_usuarios/${userComment.efa005Imagem}`}
							hover={false}
							width={20}
							height={20}
						/>
						{userComment.efa005Nome} <small>[{userComment.efa005Email}]</small>
					</button>)}
				</UlUsers>
				<RCCGrid
					container
					item
					justifyContent="flex-end"
				>
					<CheckPermission minimumPermissionRequired={PermissionEnum.VIEW}>
						<RCCTooltip
							color="green"
							text={commentAnswerId ? translate("Responder comentário") : translate("Adicionar comentário")}
						>
							<RCCButton
								color="green"
								size="sm"
								type="submit"
								height={24}
								onClick={onSubmit}
								loading={loadingButton}
								loadingText={translate("Carregando")}
								disabled={disabled}
							>
								{translate("Salvar")}
							</RCCButton>
						</RCCTooltip>
					</CheckPermission>
					<RCCButton
						color="grey"
						size="sm"
						height={24}
						onClick={() => {setValue("answerComment", ""); setCommentAnswerId(0); }}
						disabled={disabled}
					>
						{translate("Cancelar")}
					</RCCButton>
							
				</RCCGrid>
			</AnswerDiv>			
		</RCCGrid>
	);
};

export default Comment;
